import deliveryOptionsUtils from 'utils/DeliveryOptions';
import { SAME_DAY_FILTER_START } from 'constants/UpperFunnel';

const { isNDDNewDeliveryName } = deliveryOptionsUtils;

/**
 * Overrides Same-Day Delivery filter refinement value display text to "Fast Delivery"
 * when the NDD new delivery name feature flag is enabled (GUAR-7559).
 *
 * This utility ensures consistent override logic across all filter components
 * (FilterGroup, FilterPills, etc.) without code duplication.
 *
 * Rebuilds `refinementDisplayNameAndValue` from component parts rather than regex replacement
 * to avoid brittleness from API copy variations.
 *
 * @param {Object} value - Refinement value object from BXS API
 * @param {string} value.refinementValue - Filter refinement value (e.g., "filters[SameDay]=94102")
 * @param {string} value.refinementValueDisplayName - Display name for the filter
 * @param {string} [value.refinementDisplayNameAndValue] - Display name with appended value (e.g., "Same-Day Delivery: 94102")
 * @param {string} [value.chicletDisplayName] - Display name for chiclet/pill
 * @param {Function} getText - Localization function to retrieve locale-specific text
 * @returns {Object} - Original value or overridden value with updated display fields
 *
 * @example
 * import { getOverriddenRefinementValue } from 'utils/FilterUtils';
 * import { getLocalisedDataFromResource } from 'utils/LanguageLocale';
 * import EN from 'components/Catalog/Filters/locales/Filters_en_US';
 * import FR from 'components/Catalog/Filters/locales/Filters_fr_CA';
 *
 * const getText = getLocalisedDataFromResource(EN, FR);
 * const overriddenValue = getOverriddenRefinementValue(value, getText);
 */
function getOverriddenRefinementValue(value, getText) {
    const isNDDNewDeliveryNameEnabled = isNDDNewDeliveryName();

    // Check if this value is Same-Day Delivery filter
    // Pattern: refinementValue starts with "filters[SameDay]="
    const isSameDayFilter = value.refinementValue?.startsWith(SAME_DAY_FILTER_START);

    if (isNDDNewDeliveryNameEnabled && isSameDayFilter) {
        const fastDeliveryText = getText('fastDelivery');

        // Rebuild refinementDisplayNameAndValue from component parts instead of regex replacement
        // This is more robust than regex replacing hardcoded strings, which breaks on copy variations
        let refinementDisplayNameAndValue;

        if (value.refinementDisplayNameAndValue) {
            // Extract the location/zip from the refinementValue
            // Pattern: "filters[SameDay]=94102" -> "94102"
            // Use SAME_DAY_FILTER_START constant to avoid hardcoding the filter key
            const zipCode = value.refinementValue.slice(SAME_DAY_FILTER_START.length + 1); // +1 to skip the '=' sign

            // Rebuild as "Fast Delivery: 94102"
            refinementDisplayNameAndValue = zipCode ? `${fastDeliveryText}: ${zipCode}` : fastDeliveryText;
        }

        return {
            ...value,
            refinementValueDisplayName: fastDeliveryText,
            chicletDisplayName: fastDeliveryText,
            ...(refinementDisplayNameAndValue && { refinementDisplayNameAndValue })
        };
    }

    return value;
}

export { getOverriddenRefinementValue };
